import { useCallback, useEffect, useMemo, useState } from 'react';
import { authFetch } from '../../../utils/authFetch';

/**
 * Workspace Settings → Availability.
 *
 * Two deliberately separate concerns:
 *
 *   WORKSPACE POLICY — timezone (the workspace's existing localization default),
 *   read freshness, and optional auto-close of forgotten check-ins.
 *
 *   MEMBER SCHEDULE — each member's weekly working hours.
 *
 * Nothing here invents data. An unconfigured member shows an EMPTY week, never a
 * guessed 9–5, and auto-close stays off unless an administrator turns it on.
 * Only workspace owners and admins can manage these settings; the API enforces
 * that, and this tab simply reports it.
 */

interface AvailabilityTabProps {
  workspaceId: string;
  authToken: string;
}

interface Policy {
  source: 'default' | 'configured';
  autoClockOutEnabled: boolean;
  leaveRequiresApproval: boolean;
  autoClockOutAfterHours: number | null;
  readFreshnessHours: number;
  configuredReadFreshnessHours: number | null;
  validFrom: string | null;
}

interface Limits {
  minHours: number;
  maxHours: number;
  defaultReadFreshnessHours: number;
  recommendedAutoClockOutHours: number;
}

interface Person {
  membershipId: string;
  userId: string;
  name: string | null;
  email: string;
  role: string;
}

interface DayRow {
  enabled: boolean;
  start: string;
  end: string;
}

/** Engine convention: 0 = Sunday … 6 = Saturday. Displayed Monday-first. */
export const WEEKDAYS: ReadonlyArray<{ weekday: number; label: string }> = [
  { weekday: 1, label: 'Monday' },
  { weekday: 2, label: 'Tuesday' },
  { weekday: 3, label: 'Wednesday' },
  { weekday: 4, label: 'Thursday' },
  { weekday: 5, label: 'Friday' },
  { weekday: 6, label: 'Saturday' },
  { weekday: 0, label: 'Sunday' },
];

/** An empty week: no day enabled, no times. Never a guessed default schedule. */
export function emptyWeek(): Record<number, DayRow> {
  const week: Record<number, DayRow> = {};
  for (const { weekday } of WEEKDAYS) week[weekday] = { enabled: false, start: '', end: '' };
  return week;
}

export function isValidTimezone(tz: string): boolean {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

type LoadState = 'loading' | 'ready' | 'forbidden' | 'disabled' | 'error';

export function AvailabilityTab({ workspaceId, authToken }: AvailabilityTabProps) {
  const [state, setState] = useState<LoadState>('loading');
  const [limits, setLimits] = useState<Limits | null>(null);
  const [policy, setPolicy] = useState<Policy | null>(null);

  // Workspace policy form
  const [timezone, setTimezone] = useState('');
  const [savedTimezone, setSavedTimezone] = useState('');
  const [readFreshness, setReadFreshness] = useState('');
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(false);
  const [leaveRequiresApproval, setLeaveRequiresApproval] = useState(true);
  const [autoCloseHours, setAutoCloseHours] = useState('');
  const [policyMessage, setPolicyMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [savingPolicy, setSavingPolicy] = useState(false);

  // Member schedule form
  const [people, setPeople] = useState<Person[]>([]);
  const [selectedMembershipId, setSelectedMembershipId] = useState('');
  const [week, setWeek] = useState<Record<number, DayRow>>(emptyWeek());
  const [memberTimezone, setMemberTimezone] = useState('');
  const [memberConfigured, setMemberConfigured] = useState(false);
  const [memberValidFrom, setMemberValidFrom] = useState<string | null>(null);
  const [splitShiftLocked, setSplitShiftLocked] = useState(false);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberMessage, setMemberMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [savingMember, setSavingMember] = useState(false);

  const applyPolicy = (p: Policy) => {
    setPolicy(p);
    setReadFreshness(p.configuredReadFreshnessHours === null ? '' : String(p.configuredReadFreshnessHours));
    setAutoCloseEnabled(p.autoClockOutEnabled);
    // Absent (an older server, or a partial payload) means the pre-policy
    // behaviour: approval required. Never let it become undefined, which would
    // silently turn a controlled checkbox into an uncontrolled one.
    setLeaveRequiresApproval(p.leaveRequiresApproval ?? true);
    setAutoCloseHours(p.autoClockOutAfterHours === null ? '' : String(p.autoClockOutAfterHours));
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const policyRes = await authFetch('/api/availability/attendance-policy', authToken);
        if (policyRes.status === 403) { if (!cancelled) setState('forbidden'); return; }
        if (policyRes.status === 404) { if (!cancelled) setState('disabled'); return; }
        if (!policyRes.ok) { if (!cancelled) setState('error'); return; }
        const policyBody = await policyRes.json();

        const [wsRes, peopleRes] = await Promise.all([
          authFetch(`/api/workspaces/${workspaceId}`, authToken),
          authFetch(`/api/workspaces/${workspaceId}/people`, authToken),
        ]);
        const wsBody = wsRes.ok ? await wsRes.json() : null;
        const peopleBody = peopleRes.ok ? await peopleRes.json() : null;
        if (cancelled) return;

        setLimits(policyBody.limits);
        applyPolicy(policyBody.policy);
        const tz = wsBody?.workspace?.defaultTimezone ?? '';
        setTimezone(tz);
        setSavedTimezone(tz);
        setPeople((peopleBody?.members ?? []).filter((m: Person) => m.membershipId));
        setState('ready');
      } catch {
        if (!cancelled) setState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [workspaceId, authToken]);

  const policyErrors = useMemo(() => {
    const errors: string[] = [];
    if (!limits) return errors;
    const inRange = (v: string) => {
      const n = Number(v);
      return Number.isInteger(n) && n >= limits.minHours && n <= limits.maxHours;
    };
    if (timezone && !isValidTimezone(timezone)) errors.push('Timezone must be a valid IANA timezone, e.g. America/New_York.');
    if (readFreshness !== '' && !inRange(readFreshness)) {
      errors.push(`Attendance freshness must be a whole number of hours between ${limits.minHours} and ${limits.maxHours}.`);
    }
    if (autoCloseEnabled && autoCloseHours === '') errors.push('Choose an auto-close threshold, or turn auto-close off.');
    if (autoCloseHours !== '' && !inRange(autoCloseHours)) {
      errors.push(`Auto-close threshold must be a whole number of hours between ${limits.minHours} and ${limits.maxHours}.`);
    }
    return errors;
  }, [limits, timezone, readFreshness, autoCloseEnabled, autoCloseHours]);

  const toggleAutoClose = (on: boolean) => {
    setAutoCloseEnabled(on);
    // Surface the recommendation only when the administrator opts in — never as a
    // silent default on a workspace that has not chosen auto-close.
    if (on && autoCloseHours === '' && limits) setAutoCloseHours(String(limits.recommendedAutoClockOutHours));
  };

  const savePolicy = async () => {
    if (policyErrors.length > 0) return;
    setSavingPolicy(true);
    setPolicyMessage(null);
    try {
      if (timezone !== savedTimezone) {
        const tzRes = await authFetch(`/api/workspaces/${workspaceId}/localization`, authToken, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ defaultTimezone: timezone === '' ? null : timezone }),
        });
        if (!tzRes.ok) throw new Error('Could not save the workspace timezone.');
        setSavedTimezone(timezone);
      }
      const res = await authFetch('/api/availability/attendance-policy', authToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          autoClockOutEnabled: autoCloseEnabled,
          leaveRequiresApproval,
          autoClockOutAfterHours: autoCloseHours === '' ? null : Number(autoCloseHours),
          readFreshnessHours: readFreshness === '' ? null : Number(readFreshness),
          expectedCurrentValidFrom: policy?.validFrom ?? null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) throw new Error('These settings were changed by someone else. Reload and try again.');
      if (!res.ok) throw new Error(body.issues?.join(' ') || body.error || 'Could not save availability settings.');
      applyPolicy(body.policy);
      setPolicyMessage({ kind: 'ok', text: 'Availability settings saved.' });
    } catch (e) {
      setPolicyMessage({ kind: 'error', text: e instanceof Error ? e.message : 'Could not save availability settings.' });
    } finally {
      setSavingPolicy(false);
    }
  };

  const loadMember = useCallback(async (membershipId: string) => {
    setSelectedMembershipId(membershipId);
    setMemberMessage(null);
    setSplitShiftLocked(false);
    setWeek(emptyWeek());
    setMemberConfigured(false);
    setMemberValidFrom(null);
    // Prefill only with the workspace's OWN configured timezone, if it has one.
    setMemberTimezone(savedTimezone);
    if (!membershipId) return;
    setMemberLoading(true);
    try {
      const res = await authFetch(`/api/availability/config/${membershipId}`, authToken);
      if (res.status === 404) return; // not configured → empty week, nothing invented
      if (!res.ok) throw new Error('Could not load working hours.');
      const { config } = await res.json();
      const next = emptyWeek();
      const perDay = new Map<number, number>();
      for (const w of config.workingHours.weekly as Array<{ weekday: number; start: string; end: string }>) {
        perDay.set(w.weekday, (perDay.get(w.weekday) ?? 0) + 1);
        next[w.weekday] = { enabled: true, start: w.start, end: w.end };
      }
      // This editor holds one window per day. Saving over split shifts would
      // silently delete them, so refuse to edit rather than lose data.
      if ([...perDay.values()].some((n) => n > 1)) setSplitShiftLocked(true);
      setWeek(next);
      setMemberTimezone(config.timezone ?? '');
      setMemberConfigured(true);
      setMemberValidFrom(config.effective?.validFrom ?? null);
    } catch (e) {
      setMemberMessage({ kind: 'error', text: e instanceof Error ? e.message : 'Could not load working hours.' });
    } finally {
      setMemberLoading(false);
    }
  }, [authToken, savedTimezone]);

  const memberErrors = useMemo(() => {
    const errors: string[] = [];
    if (!selectedMembershipId) return errors;
    if (!isValidTimezone(memberTimezone)) errors.push('Member timezone must be a valid IANA timezone.');
    for (const { weekday, label } of WEEKDAYS) {
      const d = week[weekday]!;
      if (!d.enabled) continue;
      if (!d.start || !d.end) errors.push(`${label}: set both a start and an end time.`);
      else if (d.start >= d.end) errors.push(`${label}: start must be before end.`);
    }
    return errors;
  }, [selectedMembershipId, memberTimezone, week]);

  const saveMember = async () => {
    if (!selectedMembershipId || memberErrors.length > 0 || splitShiftLocked) return;
    setSavingMember(true);
    setMemberMessage(null);
    try {
      const weekly = WEEKDAYS
        .filter(({ weekday }) => week[weekday]!.enabled)
        .map(({ weekday }) => ({ weekday, start: week[weekday]!.start, end: week[weekday]!.end }));
      const res = await authFetch(`/api/availability/config/${selectedMembershipId}`, authToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workingHours: { weekly },
          timezone: memberTimezone,
          expectedCurrentValidFrom: memberValidFrom,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) throw new Error('These working hours were changed by someone else. Reload and try again.');
      if (!res.ok) throw new Error(body.issues?.join(' ') || body.error || 'Could not save working hours.');
      setMemberConfigured(true);
      setMemberValidFrom(body.config?.effective?.validFrom ?? null);
      setMemberMessage({ kind: 'ok', text: 'Working hours saved.' });
    } catch (e) {
      setMemberMessage({ kind: 'error', text: e instanceof Error ? e.message : 'Could not save working hours.' });
    } finally {
      setSavingMember(false);
    }
  };

  const setDay = (weekday: number, patch: Partial<DayRow>) =>
    setWeek((w) => ({ ...w, [weekday]: { ...w[weekday]!, ...patch } }));

  if (state === 'loading') return <div className="text-sm text-gray-500">Loading availability settings…</div>;
  if (state === 'forbidden') {
    return (
      <div className="rounded-lg border border-gray-200 p-6 text-sm text-gray-600" role="status">
        Only workspace owners and admins can manage availability settings.
      </div>
    );
  }
  if (state === 'disabled') {
    return (
      <div className="rounded-lg border border-gray-200 p-6 text-sm text-gray-600" role="status">
        Availability is not enabled for this environment.
      </div>
    );
  }
  if (state === 'error' || !limits || !policy) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700" role="alert">
        Could not load availability settings.
      </div>
    );
  }

  const selectedPerson = people.find((p) => p.membershipId === selectedMembershipId);

  return (
    <div className="space-y-8">
      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Workspace availability</h2>
        <p className="mt-1 text-sm text-gray-500">
          How attendance is interpreted across this workspace.
          {policy.source === 'default' && ' No policy has been set yet — Central defaults apply.'}
        </p>

        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Timezone</span>
            <input
              aria-label="Workspace timezone"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
              placeholder="e.g. America/New_York"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value.trim())}
            />
            <span className="mt-1 block text-xs text-gray-500">Default for new member schedules.</span>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-gray-700">Attendance freshness (hours)</span>
            <input
              aria-label="Attendance freshness hours"
              type="number"
              min={limits.minHours}
              max={limits.maxHours}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
              placeholder={`${limits.defaultReadFreshnessHours} (default)`}
              value={readFreshness}
              onChange={(e) => setReadFreshness(e.target.value)}
            />
            <span className="mt-1 block text-xs text-gray-500">
              A check-in older than this is no longer trusted as “working now”. It is not closed.
            </span>
          </label>
        </div>

        <div className="mt-6 rounded-md border border-gray-200 p-4">
          <label className="flex items-center gap-3 text-sm">
            <input
              aria-label="Require approval for leave"
              type="checkbox"
              checked={leaveRequiresApproval}
              onChange={(e) => setLeaveRequiresApproval(e.target.checked)}
              data-testid="policy-leave-requires-approval"
            />
            <span className="font-medium text-gray-700">Require approval for time off</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            {leaveRequiresApproval
              ? 'On. A member\u2019s time-off request is recorded but does not affect their availability until an owner or admin approves it.'
              : 'Off. A member\u2019s time off takes effect immediately \u2014 they are marked unavailable for those dates without needing approval.'}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Changing this affects new requests only. Time off that is already pending stays pending, and
            time off that is already approved stays approved.
          </p>
        </div>

        <div className="mt-6 rounded-md border border-gray-200 p-4">
          <label className="flex items-center gap-3 text-sm">
            <input
              aria-label="Auto-close forgotten check-ins"
              type="checkbox"
              checked={autoCloseEnabled}
              onChange={(e) => toggleAutoClose(e.target.checked)}
            />
            <span className="font-medium text-gray-700">Auto-close forgotten check-ins</span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            Off by default. Applies only to check-ins started after you turn it on — earlier sessions are never changed.
          </p>
          {autoCloseEnabled && (
            <label className="mt-3 block text-sm">
              <span className="font-medium text-gray-700">Close after (hours)</span>
              <input
                aria-label="Auto-close threshold hours"
                type="number"
                min={limits.minHours}
                max={limits.maxHours}
                className="mt-1 w-40 rounded-md border border-gray-300 px-3 py-2"
                value={autoCloseHours}
                onChange={(e) => setAutoCloseHours(e.target.value)}
              />
              <span className="ml-2 text-xs text-gray-500">Recommended: {limits.recommendedAutoClockOutHours}</span>
            </label>
          )}
        </div>

        {policyErrors.length > 0 && (
          <ul className="mt-4 list-disc pl-5 text-sm text-red-600" role="alert">
            {policyErrors.map((e) => <li key={e}>{e}</li>)}
          </ul>
        )}
        {policyMessage && (
          <p className={`mt-4 text-sm ${policyMessage.kind === 'ok' ? 'text-green-700' : 'text-red-600'}`} role="status">
            {policyMessage.text}
          </p>
        )}
        <button
          type="button"
          className="mt-6 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          disabled={savingPolicy || policyErrors.length > 0}
          onClick={savePolicy}
        >
          {savingPolicy ? 'Saving…' : 'Save workspace availability'}
        </button>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white p-6">
        <h2 className="text-lg font-semibold text-gray-900">Member working hours</h2>
        <p className="mt-1 text-sm text-gray-500">Set when each member is scheduled to work.</p>

        <label className="mt-4 block text-sm">
          <span className="font-medium text-gray-700">Member</span>
          <select
            aria-label="Member"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            value={selectedMembershipId}
            onChange={(e) => loadMember(e.target.value)}
          >
            <option value="">Select a member…</option>
            {people.map((p) => (
              <option key={p.membershipId} value={p.membershipId}>
                {p.name || p.email}
              </option>
            ))}
          </select>
        </label>

        {selectedPerson && !memberLoading && (
          <div className="mt-6">
            {!memberConfigured && (
              <p className="mb-4 text-sm text-gray-600" role="status">
                No working hours set for {selectedPerson.name || selectedPerson.email} yet.
              </p>
            )}
            {splitShiftLocked && (
              <p className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800" role="alert">
                This member has more than one working window on a day. This editor holds one window per day, so
                editing is disabled here to avoid deleting shifts.
              </p>
            )}

            <label className="mb-4 block text-sm">
              <span className="font-medium text-gray-700">Member timezone</span>
              <input
                aria-label="Member timezone"
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                placeholder="e.g. America/New_York"
                value={memberTimezone}
                disabled={splitShiftLocked}
                onChange={(e) => setMemberTimezone(e.target.value.trim())}
              />
            </label>

            <table className="w-full text-sm">
              <tbody>
                {WEEKDAYS.map(({ weekday, label }) => {
                  const d = week[weekday]!;
                  return (
                    <tr key={weekday} className="border-t border-gray-100">
                      <td className="py-2 pr-4">
                        <label className="flex items-center gap-2">
                          <input
                            aria-label={`${label} working`}
                            type="checkbox"
                            checked={d.enabled}
                            disabled={splitShiftLocked}
                            onChange={(e) => setDay(weekday, { enabled: e.target.checked })}
                          />
                          {label}
                        </label>
                      </td>
                      <td className="py-2 pr-2">
                        <input
                          aria-label={`${label} start`}
                          type="time"
                          className="rounded-md border border-gray-300 px-2 py-1 disabled:bg-gray-50"
                          value={d.start}
                          disabled={!d.enabled || splitShiftLocked}
                          onChange={(e) => setDay(weekday, { start: e.target.value })}
                        />
                      </td>
                      <td className="py-2">
                        <input
                          aria-label={`${label} end`}
                          type="time"
                          className="rounded-md border border-gray-300 px-2 py-1 disabled:bg-gray-50"
                          value={d.end}
                          disabled={!d.enabled || splitShiftLocked}
                          onChange={(e) => setDay(weekday, { end: e.target.value })}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {memberErrors.length > 0 && (
              <ul className="mt-4 list-disc pl-5 text-sm text-red-600" role="alert">
                {memberErrors.map((e) => <li key={e}>{e}</li>)}
              </ul>
            )}
            {memberMessage && (
              <p className={`mt-4 text-sm ${memberMessage.kind === 'ok' ? 'text-green-700' : 'text-red-600'}`} role="status">
                {memberMessage.text}
              </p>
            )}
            <button
              type="button"
              className="mt-6 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              disabled={savingMember || memberErrors.length > 0 || splitShiftLocked}
              onClick={saveMember}
            >
              {savingMember ? 'Saving…' : 'Save working hours'}
            </button>
          </div>
        )}
        {memberLoading && <p className="mt-6 text-sm text-gray-500">Loading working hours…</p>}
      </section>
    </div>
  );
}
